import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Dropdown from '../../common/Dropdown/Dropdown';
import Icon from '../../common/Icon/Icon';
import { IconKeys } from '../../common/Icon/IconKeys';
import S from './FleetHealthStyles';
import useGetFleetId from '../../../API/hooks/useGetFleetId';
import KnobChartWidget from '../../common/KnobChartWidget/KnobChartWidget';
import GradientFlow from '../../common/GradientFlow/GradientFlow';
import useGetVehiclesScores from '../../../API/hooks/vehicles/useGetVehiclesScores';
import useGetVehicles from '../../../API/hooks/vehicles/useGetVehicles';
import { useEntityScores, ComputedScores } from '../../../API/hooks/useEntityScores';
import { useFleetDayScores } from '../../../API/hooks/vehicles/useFleetDayScores';
import { getDaysBetweenDates, getEndOfTheDay } from '../../../utils/dateUtils';
import GlobalSpinner from '../../common/GlobalSpinner/GlobalSpinner';
import { queryFilterByGroups } from '../../../utils/queryFilters';

const SCORE_FIELD: Record<string, string> = {
  daily: 'scoreDaily',
  weekly: 'scoreWeekly',
  monthly: 'scoreMonthly',
};

const PERIOD_LABEL: Record<string, string> = {
  daily: 'Today',
  weekly: 'This week',
  monthly: 'This month',
  all: 'All time',
};

function FleetHealth({ timeRange, groupFilters }: any): JSX.Element {
  const fleetId = useGetFleetId();
  let showHours = false;
  let period = 'monthly';
  let startDate = timeRange.startDate?.[0];
  const endDate = timeRange.endDate?.[0] || getEndOfTheDay().toISOString();

  if (timeRange?.period?.[0]) {
    if (timeRange.period[0] !== 'custom') {
      period = timeRange.period?.[0];
    } else {
      period = `&from=${startDate}&to=${endDate}`;
    }
  }

  const startYear = startDate ? new Date(startDate).getFullYear() : 0;
  if (!startDate || startYear < 2000) {
    const end = new Date(endDate);
    const limitedStart = new Date(end);
    limitedStart.setDate(end.getDate() - 65);
    startDate = limitedStart.toISOString();
  }

  if (period === 'daily' || getDaysBetweenDates(startDate, endDate) < 1) {
    showHours = true;
  }

  const dayDate = showHours ? new Date(startDate).toISOString().slice(0, 10) : '';

  const { data: vehiclesScores, isLoading: isChartLoading } = useGetVehiclesScores(
    fleetId,
    startDate,
    endDate,
    queryFilterByGroups(groupFilters),
  );

  const { data: allVehiclesData } = useGetVehicles(fleetId, '?limit=1000&skip=0');
  const vehicleIds = useMemo(() => (allVehiclesData?.results ?? []).map((v) => v._id), [allVehiclesData]);

  const { scores: fleetDayScores, isLoading: isDayScoresLoading } = useFleetDayScores(fleetId, vehicleIds, dayDate);
  const { scores: computedScores, isLoading: isScoresLoading } = useEntityScores(fleetId, vehicleIds, 'vehicle');

  const effectivePeriod = showHours ? 'daily' : period;
  const scoreField = SCORE_FIELD[effectivePeriod] ?? 'score';
  const isAllTime = timeRange.period?.[0] === 'custom' && timeRange.startDate?.[0] === new Date(0).toISOString();
  const periodLabel = isAllTime ? PERIOD_LABEL.all : PERIOD_LABEL[effectivePeriod];

  const averageScore = useMemo<number | null>(() => {
    if (isScoresLoading || vehicleIds.length === 0) return null;
    const valid = vehicleIds
      .map((id) => (computedScores.get(id)?.[scoreField as keyof ComputedScores] ?? null))
      .filter((s): s is number => s != null && s > 0);
    return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : null;
  }, [computedScores, scoreField, isScoresLoading, vehicleIds]);

  // keep previous value while loading to avoid N/A flash
  const lastScoreRef = useRef<number | null>(null);
  if (isScoresLoading === false) {
    lastScoreRef.current = averageScore;
  }
  const displayScore = lastScoreRef.current;

  const chartScores = showHours ? fleetDayScores : vehiclesScores?.scores;
  const isLoading = showHours ? isDayScoresLoading : isChartLoading;

  const navigate = useNavigate();
  const handleClick = (): void => {
    navigate('/vehicles');
  };

  return (
    <S.Wrapper>
      <div className="header">
        <h3 className="header-title">
          Fleet Health
          {periodLabel && ` • ${periodLabel}`}
        </h3>
        <Dropdown
          target={(
            <div className="dropdown-icon">
              <Icon iconKey={IconKeys.EXTEND} />
            </div>
          )}
        >
          <ul className="widget-menu">
            <li>
              <button onClick={handleClick} type="button">
                <Icon iconKey={IconKeys.VIEW_ALL} />
                <p>View all</p>
              </button>
            </li>
            {/* <li>
              <Icon iconKey={IconKeys.SETTINGS_OUTLINED} />
              <p>Settings</p>
            </li>
            <li>
              <Icon iconKey={IconKeys.MOVE} />
              <p>Move</p>
            </li> */}
          </ul>
        </Dropdown>
      </div>
      <div className="fleet-health-container">
        {isLoading ? (<GlobalSpinner />) : (
          <>
            <div className="fleet-health-chart">
              <GradientFlow data={chartScores} showHours={showHours} />
            </div>
            <div className="fleet-health-score">
              <KnobChartWidget
                value={displayScore != null ? Math.round(displayScore) : 'N/A'}
                max={100}
                subtitle={periodLabel ? `Average vehicles score • ${periodLabel}` : 'Average vehicles score'}
              />
            </div>
          </>
        )}
      </div>
    </S.Wrapper>
  );
}

export default FleetHealth;
